import { DomainError } from '../errors.js';

/**
 * Kütüphaneye yüklenebilen dosya türleri (`dokuman-kutuphanesi.md` §4; ADR 0027 §5): DOCX, XLSX,
 * PDF, JPG ve PNG. Makrolu Office türleri (DOCM, XLSM) ve makro içeren dosyalar reddedilir
 * [sorular #35]. Tür, uzantıdan seçilir ve içerik imzasıyla doğrulanır; içerik imzasının
 * okunması (baş ve ZIP dizini) sunucunun işidir, bu modül yalnız baytları yorumlar.
 */
export const ALLOWED_FILE_TYPES = {
  docx: {
    extensions: ['.docx'],
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
  xlsx: {
    extensions: ['.xlsx'],
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  pdf: { extensions: ['.pdf'], mimeType: 'application/pdf' },
  jpg: { extensions: ['.jpg', '.jpeg'], mimeType: 'image/jpeg' },
  png: { extensions: ['.png'], mimeType: 'image/png' },
} as const satisfies Record<string, { extensions: readonly string[]; mimeType: string }>;
export type FileKind = keyof typeof ALLOWED_FILE_TYPES;
export const FILE_KINDS = Object.keys(ALLOWED_FILE_TYPES) as FileKind[];

/**
 * Doküman revizyonu dosyasının (ana dosya ve ekler) üst sınırı: 1 GiB (ADR 0028 EK; ADR 0007 §2
 * bu kadarıyla değişir). Diğer yüklemeler 256 MB'ta kalır.
 */
export const MAX_DOCUMENT_FILE_BYTES = 1024 * 1024 * 1024;

/** Office paketinde okunan her XML parçasının sınırı; aşan `FILE_TYPE_MISMATCH` (ZIP bombası). */
export const MAX_OFFICE_PART_BYTES = 1024 * 1024;
/** Gömülü Office paketinin sınırı (bir düzey denetlenir). */
export const MAX_EMBEDDED_PACKAGE_BYTES = 16 * 1024 * 1024;

/** İçerik imzası için okunacak baş baytları. */
export const FILE_SIGNATURE_BYTES = 8;

/** ZIP dizin sonu kaydını bulmak için okunacak kuyruk: kayıt (22) + en uzun yorum + ZIP64 bulucusu. */
export const ZIP_TAIL_BYTES = 22 + 0xffff + 20;

/** Dosya adı en çok bu kadar karakter. */
export const MAX_FILE_NAME_LENGTH = 255;

export function assertFileName(name: string): void {
  if (
    name.trim() === '' ||
    name.length > MAX_FILE_NAME_LENGTH ||
    /[/\\]/.test(name) ||
    // biome-ignore lint/suspicious/noControlCharactersInRegex: denetim karakterleri reddedilir
    /[\u0000-\u001f\u007f]/.test(name) ||
    name === '.' ||
    name === '..'
  ) {
    throw new DomainError('INVALID_FILE_NAME');
  }
}

/** Uzantıdan dosya türü; listede olmayan tür `UNSUPPORTED_FILE_TYPE`. */
export function fileKindOf(fileName: string): FileKind {
  const dot = fileName.lastIndexOf('.');
  const extension = dot < 0 ? '' : fileName.slice(dot).toLowerCase();
  for (const kind of FILE_KINDS) {
    if ((ALLOWED_FILE_TYPES[kind].extensions as readonly string[]).includes(extension)) return kind;
  }
  // Çözüm: Word ya da Excel'de makrosuz .docx/.xlsx olarak kaydetmek (ADR 0028 #35)
  throw new DomainError('UNSUPPORTED_FILE_TYPE', extension || fileName, 'extension');
}

/** Office 97–2003 ikili biçimleri: kabul edilmez; .docx/.xlsx olarak kaydedilir (ADR 0055 §2). */
const OLD_OFFICE_EXTENSIONS = ['.doc', '.dot', '.xls', '.xlt'];
/** Makro taşıyan Office türleri (ADR 0028 #35); `.xlsb` ikili ve makro taşıyabilir. */
const MACRO_EXTENSIONS = ['.docm', '.dotm', '.xlsm', '.xltm', '.xlsb'];

/**
 * Seçilen dosyanın türü yüklemeden önce: kabul ediliyorsa `null`, değilse kullanıcıya ne
 * yapacağını söyleyen kod (M14b; ADR 0055 §2) [sorular #341]. Yalnız erken uyarıdır; sunucunun
 * içerik ve makro denetimi (`assertFileSignature`) tek otoritedir.
 */
export function uploadFileProblem(
  fileName: string,
): null | 'OLD_OFFICE_FORMAT' | 'MACRO_FILE' | 'UNSUPPORTED_FILE_TYPE' {
  const dot = fileName.lastIndexOf('.');
  const extension = dot < 0 ? '' : fileName.slice(dot).toLowerCase();
  if (OLD_OFFICE_EXTENSIONS.includes(extension)) return 'OLD_OFFICE_FORMAT';
  if (MACRO_EXTENSIONS.includes(extension)) return 'MACRO_FILE';
  try {
    fileKindOf(fileName);
    return null;
  } catch {
    return 'UNSUPPORTED_FILE_TYPE';
  }
}

export function assertFileSize(size: number): void {
  if (size <= 0) throw new DomainError('EMPTY_FILE');
  if (size > MAX_DOCUMENT_FILE_BYTES) throw new DomainError('FILE_TOO_LARGE', String(size));
}

const startsWith = (bytes: Uint8Array, prefix: readonly number[]) =>
  bytes.length >= prefix.length && prefix.every((byte, i) => bytes[i] === byte);

const PDF = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-
const PNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPG = [0xff, 0xd8, 0xff];
const ZIP = [0x50, 0x4b, 0x03, 0x04];

/** Office Open XML paketinin ana parçası. */
const OOXML_MAIN: Readonly<Record<'docx' | 'xlsx', string>> = {
  docx: 'word/document.xml',
  xlsx: 'xl/workbook.xml',
};

/** VBA projesi: makro içeren Office paketinin işareti [sorular #35]. */
const MACRO_PART = /(^|\/)vbaproject\.bin$/i;

/**
 * İçerik imzası uzantıyla uyuşmalı (`FILE_TYPE_MISMATCH`). Office dosyasında ZIP giriş adları
 * gerekir: ana parça bulunmalı, makro (VBA projesi) bulunmamalı (`UNSUPPORTED_FILE_TYPE`).
 * @param head Dosyanın ilk baytları (en az `FILE_SIGNATURE_BYTES`).
 * @param zipEntries Office türlerinde ZIP merkezi dizinindeki adlar; diğerlerinde `null`.
 */
export function assertFileSignature(
  kind: FileKind,
  head: Uint8Array,
  zipEntries: readonly string[] | null,
): void {
  switch (kind) {
    case 'pdf':
      if (!startsWith(head, PDF)) throw new DomainError('FILE_TYPE_MISMATCH', kind);
      return;
    case 'png':
      if (!startsWith(head, PNG)) throw new DomainError('FILE_TYPE_MISMATCH', kind);
      return;
    case 'jpg':
      if (!startsWith(head, JPG)) throw new DomainError('FILE_TYPE_MISMATCH', kind);
      return;
    case 'docx':
    case 'xlsx': {
      if (!startsWith(head, ZIP) || zipEntries === null) {
        throw new DomainError('FILE_TYPE_MISMATCH', kind);
      }
      if (zipEntries.some((name) => MACRO_PART.test(name))) {
        throw new DomainError('UNSUPPORTED_FILE_TYPE', 'macro', 'macro');
      }
      if (!zipEntries.includes(OOXML_MAIN[kind])) throw new DomainError('FILE_TYPE_MISMATCH', kind);
      return;
    }
  }
}

const EOCD = 0x06054b50;
const ZIP64_LOCATOR = 0x07064b50;
const ZIP64_EOCD = 0x06064b50;
const CENTRAL_HEADER = 0x02014b50;

function view(bytes: Uint8Array) {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

function bigToNumber(value: bigint): number {
  if (value > BigInt(Number.MAX_SAFE_INTEGER)) throw new DomainError('FILE_TYPE_MISMATCH', 'zip');
  return Number(value);
}

/**
 * ZIP merkezi dizininin dosyadaki yeri ve dizin sonunun bildirdiği giriş sayısı, dosyanın
 * kuyruğundan (`ZIP_TAIL_BYTES`). ZIP64 dizin sonu kuyruğun içindeyse o kullanılır. Dizin sonu
 * bulunamazsa `FILE_TYPE_MISMATCH`.
 * @param tail Dosyanın son baytları.
 * @param fileSize Dosyanın tam boyutu.
 */
export function zipDirectoryLocation(
  tail: Uint8Array,
  fileSize: number,
): { offset: number; size: number; entries: number } {
  const data = view(tail);
  const tailStart = fileSize - tail.length;
  for (let i = tail.length - 22; i >= 0; i--) {
    if (data.getUint32(i, true) !== EOCD) continue;
    const commentLength = data.getUint16(i + 20, true);
    if (i + 22 + commentLength !== tail.length) continue;
    let size = data.getUint32(i + 12, true);
    let offset = data.getUint32(i + 16, true);
    let entries = data.getUint16(i + 10, true);
    if (size === 0xffffffff || offset === 0xffffffff || entries === 0xffff) {
      const locator = i - 20;
      if (locator < 0 || data.getUint32(locator, true) !== ZIP64_LOCATOR) {
        throw new DomainError('FILE_TYPE_MISMATCH', 'zip64');
      }
      const record = bigToNumber(data.getBigUint64(locator + 8, true)) - tailStart;
      if (record < 0 || record + 56 > tail.length || data.getUint32(record, true) !== ZIP64_EOCD) {
        throw new DomainError('FILE_TYPE_MISMATCH', 'zip64');
      }
      entries = bigToNumber(data.getBigUint64(record + 32, true));
      size = bigToNumber(data.getBigUint64(record + 40, true));
      offset = bigToNumber(data.getBigUint64(record + 48, true));
    }
    if (offset + size > fileSize) throw new DomainError('FILE_TYPE_MISMATCH', 'zip');
    return { offset, size, entries };
  }
  throw new DomainError('FILE_TYPE_MISMATCH', 'zip');
}

function decodeLatin1(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
}

/** Ortamdan bağımsız UTF-8 çözümü; bozuk dizi Latin-1 olarak okunur. */
function decodeUtf8(bytes: Uint8Array): string {
  try {
    return decodeURIComponent(
      Array.from(bytes, (b) => `%${b.toString(16).padStart(2, '0')}`).join(''),
    );
  } catch {
    return decodeLatin1(bytes);
  }
}

/** Merkezi dizindeki bir giriş: ad, sıkıştırma yöntemi, boyutlar ve yerel başlığın yeri. */
export interface ZipEntry {
  name: string;
  /** 0 sıkıştırmasız, 8 deflate. */
  method: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
}

export interface ZipEntriesOptions {
  /** Dizin sonunun bildirdiği giriş sayısı; dizinde başka sayıda giriş varsa reddedilir. */
  expectedCount?: number;
  /** Merkezi dizinin dosyadaki yeri: son girişin verisi dizinden önce bitmeli. */
  directoryOffset?: number;
}

/** Sıkıştırılmış verinin en az kapladığı yer: yerel başlık (30 bayt) ve veri. */
function minimalSpan(entry: ZipEntry): number | null {
  return entry.compressedSize === 0xffffffff ? null : 30 + entry.compressedSize;
}

/**
 * Okuyucular arasında fark yaratabilecek dizinler reddedilir (`FILE_TYPE_MISMATCH`): büyük/küçük
 * harfe duyarsız tekrar eden ad (OPC'de parça adı tekildir; bir okuyucu ilkini, öteki sonuncuyu
 * alır), dizin sonundaki sayıyla uyuşmayan giriş sayısı (dizini sayıyla yürüyen okuyucu farklı
 * görür), aynı ya da çakışan yerel başlık (tek veriyi yüzlerce kez açtıran dizin) [sorular #35].
 */
function assertConsistentZip(entries: readonly ZipEntry[], options: ZipEntriesOptions): void {
  if (options.expectedCount !== undefined && options.expectedCount !== entries.length) {
    throw new DomainError('FILE_TYPE_MISMATCH', 'zip entry count');
  }
  const names = new Set<string>();
  for (const entry of entries) {
    const key = entry.name.toLowerCase();
    if (names.has(key)) throw new DomainError('FILE_TYPE_MISMATCH', 'zip duplicate name');
    names.add(key);
  }
  const ordered = [...entries].sort((a, b) => a.localHeaderOffset - b.localHeaderOffset);
  for (const [index, entry] of ordered.entries()) {
    const next = ordered[index + 1]?.localHeaderOffset ?? options.directoryOffset;
    const span = minimalSpan(entry);
    if (next === undefined || span === null) continue;
    if (entry.localHeaderOffset + span > next) {
      throw new DomainError('FILE_TYPE_MISMATCH', 'zip overlapping entries');
    }
  }
}

/** Merkezi dizindeki girişler. Bozuk ya da tutarsız dizin `FILE_TYPE_MISMATCH`. */
export function zipEntries(directory: Uint8Array, options: ZipEntriesOptions = {}): ZipEntry[] {
  const data = view(directory);
  const entries: ZipEntry[] = [];
  let i = 0;
  while (i < directory.length) {
    if (i + 46 > directory.length || data.getUint32(i, true) !== CENTRAL_HEADER) {
      throw new DomainError('FILE_TYPE_MISMATCH', 'zip');
    }
    const flags = data.getUint16(i + 8, true);
    const nameLength = data.getUint16(i + 28, true);
    const extraLength = data.getUint16(i + 30, true);
    const commentLength = data.getUint16(i + 32, true);
    const end = i + 46 + nameLength;
    if (end > directory.length) throw new DomainError('FILE_TYPE_MISMATCH', 'zip');
    const raw = directory.subarray(i + 46, end);
    entries.push({
      name: flags & 0x0800 ? decodeUtf8(raw) : decodeLatin1(raw),
      method: data.getUint16(i + 10, true),
      compressedSize: data.getUint32(i + 20, true),
      uncompressedSize: data.getUint32(i + 24, true),
      localHeaderOffset: data.getUint32(i + 42, true),
    });
    i = end + extraLength + commentLength;
  }
  if (entries.length === 0) throw new DomainError('FILE_TYPE_MISMATCH', 'zip');
  assertConsistentZip(entries, options);
  return entries;
}

/** Merkezi dizindeki giriş adları. Bozuk ya da tutarsız dizin `FILE_TYPE_MISMATCH`. */
export function zipEntryNames(directory: Uint8Array, options: ZipEntriesOptions = {}): string[] {
  return zipEntries(directory, options).map((entry) => entry.name);
}
